import { Link } from '@tanstack/react-router'
import { buildUtilities, type UtilityKey } from '@/shared/utilities/utilities'
import { useT } from '@/shared/i18n'

const MAX_RELATED_TOOLS = 5

const RELATED_TOOL_KEYS: Partial<Record<UtilityKey, readonly UtilityKey[]>> = {
  'pdf-viewer': ['povernut', 'splitPdf', 'szhat', 'pdfToText', 'vJpg'],
  szhat: ['repair-pdf', 'pdf-metadata'],
  excelVPdf: ['htmlToPdf', 'jpgVPdf', 'szhat'],
  htmlToPdf: ['excelVPdf', 'jpgVPdf', 'png-v-pdf', 'pdfToText'],
  pdfToText: ['compare-pdf', 'htmlToPdf', 'vJpg'],
  vJpg: ['pdf-to-png', 'jpgVPdf', 'extract-images-from-pdf'],
  'pdf-to-png': ['vJpg', 'png-v-pdf', 'extract-images-from-pdf'],
  jpgVPdf: ['document-scanner', 'png-v-pdf', 'heicVPdf', 'vJpg'],
  'document-scanner': ['jpgVPdf', 'szhat'],
  'png-v-pdf': ['jpgVPdf', 'heicVPdf', 'pdf-to-png'],
  heicVPdf: ['jpgVPdf', 'png-v-pdf', 'vJpg'],
  'extract-images-from-pdf': ['vJpg', 'pdf-to-png', 'jpgVPdf'],
  povernut: ['crop-pdf', 'mergePdf', 'splitPdf', 'deletePdfPages'],
  mergePdf: ['splitPdf', 'reorder-pdf-pages', 'deletePdfPages', 'povernut'],
  splitPdf: ['mergePdf', 'deletePdfPages', 'crop-pdf', 'reorder-pdf-pages'],
  'crop-pdf': ['povernut', 'splitPdf', 'deletePdfPages', 'reorder-pdf-pages'],
  numeraciyaStranic: ['watermark-pdf', 'reorder-pdf-pages', 'mergePdf'],
  'watermark-pdf': ['numeraciyaStranic', 'protect-pdf', 'flatten-pdf'],
  deletePdfPages: ['splitPdf', 'reorder-pdf-pages', 'crop-pdf'],
  'reorder-pdf-pages': ['mergePdf', 'splitPdf', 'deletePdfPages', 'numeraciyaStranic'],
  'flatten-pdf': ['pdf-metadata', 'watermark-pdf', 'protect-pdf'],
  'pdf-metadata': ['redact-pdf', 'flatten-pdf', 'repair-pdf', 'szhat'],
  'redact-pdf': ['pdf-metadata', 'protect-pdf', 'unlockPdf', 'flatten-pdf'],
  'compare-pdf': ['pdfToText', 'mergePdf', 'splitPdf'],
  'repair-pdf': ['szhat', 'pdf-metadata', 'flatten-pdf'],
  unlockPdf: ['protect-pdf', 'flatten-pdf', 'pdf-metadata'],
  'protect-pdf': ['unlockPdf', 'watermark-pdf', 'flatten-pdf'],
}

interface Props {
  current: UtilityKey
}

export function ToolCrossPromo({ current }: Props) {
  const t = useT()
  const utilities = buildUtilities(t)
  const currentTool = utilities.find((utility) => utility.key === current)

  if (!currentTool) return null

  const explicitKeys = RELATED_TOOL_KEYS[current] ?? []
  const explicit = explicitKeys
    .map((key) => utilities.find((utility) => utility.key === key))
    .filter((utility): utility is NonNullable<typeof utility> => (
      utility != null && utility.status === 'available'
    ))
  const fallback = utilities.filter((utility) => (
    utility.key !== current &&
    utility.status === 'available' &&
    utility.taskCategory === currentTool.taskCategory &&
    !explicit.some((related) => related.key === utility.key)
  ))
  const related = (explicit.length > 0 ? explicit : fallback).slice(0, MAX_RELATED_TOOLS)

  if (related.length === 0) return null

  return (
    <section
      aria-label={t.tool.crossPromoHeading}
      className="mt-12 rounded-xl border bg-muted/30 p-6"
    >
      <h2 className="mb-4 text-base font-semibold text-foreground">
        {t.tool.crossPromoHeading}
      </h2>
      <ul className="grid gap-3 sm:grid-cols-2">
        {related.map((tool) => (
          <li key={tool.key}>
            <Link
              to={tool.path}
              className="group flex h-full items-start gap-3 rounded-lg border bg-background p-3 text-sm text-foreground transition-colors hover:border-primary/50 hover:text-primary"
            >
              <span aria-hidden className="text-xl">
                {tool.icon}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-medium">{tool.title}</span>
                <span className="block text-xs text-muted-foreground">
                  {tool.shortDescription}
                </span>
              </span>
              <span className="ml-auto self-center text-xs text-muted-foreground group-hover:text-primary">
                →
              </span>
            </Link>
          </li>
        ))}
      </ul>
      <Link
        to="/pdf-konverter"
        className="mt-5 inline-flex text-sm font-medium text-primary underline-offset-2 hover:underline"
      >
        {t.tool.crossPromoAllTools} →
      </Link>
    </section>
  )
}
